import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Loading, Empty, ScreenBoundary } from "../components/states";
import {
  Button,
  FIELD_LABEL,
  SectionHeading,
  plural,
} from "../components/kit";
import { useViewer } from "../lib/viewer";
import { useBusyAction } from "../lib/useBusyAction";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ROLES,
  ROLE_LABELS,
  ROLE_DESCRIPTIONS,
  isClinicScoped,
  type Role,
} from "@shared/roles";
import { TONE_EDGE } from "@shared/status";

/**
 * Staff and roles.
 *
 * The screen exists because of a gap in the expansion story, not because a CRUD
 * form was missing. Adding clinic #6 takes under a minute — and until this
 * screen, the clinic it produced had no manager, no assistant and no way to get
 * either, so it could never file a request. This is the second minute of that
 * demo: add the clinic, staff it, and the clinic is live.
 *
 * Three things it has to make obvious, in this order:
 *
 *  1. **Which clinics have nobody.** A clinic with no manager cannot approve,
 *     and one with no assistant cannot request. That is a working-system
 *     problem, so it sits at the top with the button that fixes it rather than
 *     being something the owner has to notice by reading a list.
 *  2. **Who each person is and what they can see** — role, clinic, and whether
 *     their sign-in is actually bound yet. A row that says nothing about
 *     sign-in invites the assumption that adding a name is enough.
 *  3. **That deactivated people are still here.** They are named on historical
 *     requests, dispatches and audit rows, so they are shown greyed rather than
 *     hidden — and the button says Reactivate, which is also the answer when an
 *     address is refused as already taken.
 *
 * Authorization is entirely the server's: `users.list`, `users.create`,
 * `users.setRole` and `users.setActive` all call `requireOwner`, and the
 * duplicate-email, last-owner, self-demotion and role/clinic rules live in
 * `convex/users.ts`. Nothing here re-implements any of it — the owner gate below
 * exists so a manager gets an explanation instead of an error card, and removing
 * it would leak nothing.
 */

interface StaffRow {
  _id: Id<"users">;
  name: string;
  email: string;
  role: string;
  clinicId: Id<"clinics"> | null;
  clinicName: string | null;
  linked: boolean;
  active: boolean;
}

interface ClinicRow {
  _id: Id<"clinics">;
  name: string;
  code: string;
  active: boolean;
}

const isRole = (value: string): value is Role =>
  (ROLES as readonly string[]).includes(value);

const roleLabel = (value: string): string =>
  isRole(value) ? ROLE_LABELS[value] : value;

/** Owner first, then the people who act on a clinic's behalf. Matches the order
 *  someone reads a staff list in: who runs this, who approves, who asks. */
const ROLE_ORDER: Record<string, number> = {
  owner: 0,
  warehouse: 1,
  manager: 2,
  assistant: 3,
};

/**
 * The badge behind a role name — three stock `<Badge>` variants, no fourth
 * palette.
 *
 * The warehouse admin and a clinic manager share one: both are trusted, neither
 * is the owner, and giving them two near-identical slates would invite the room
 * to hunt for a distinction that is not there.
 *
 * The owner's is the one that is not a bare variant, and `bg-foreground` is the
 * reason it can be honest about it: the darkest thing on the page, in the
 * theme's own ink, rather than a slate picked out of Tailwind beside it.
 * Deliberately not `variant="default"` — that is `--primary`, and teal in this
 * app means "press me". A role is not a control and not a status.
 */
const ROLE_BADGE: Record<
  string,
  { variant: "secondary" | "outline"; className?: string }
> = {
  owner: { variant: "secondary", className: "bg-foreground text-background" },
  warehouse: { variant: "secondary" },
  manager: { variant: "secondary" },
  assistant: { variant: "outline" },
};

/* -------------------------------------------------------------------------- */

export function Users() {
  // Remounting the boundary is how a failed query gets retried: Convex throws
  // during render, so there is nothing to re-run without a fresh subtree.
  const [attempt, setAttempt] = useState(0);
  return (
    <ScreenBoundary
      key={attempt}
      title="The staff list could not load"
      heading={<SectionHeading title="Staff" />}
      onRetry={() => setAttempt((n) => n + 1)}
    >
      <StaffScreen />
    </ScreenBoundary>
  );
}

function StaffScreen() {
  const viewer = useViewer();

  // The server refuses a non-owner outright. Skipping the query is a courtesy so
  // they get a sentence instead of an error card — it is not the gate.
  const isOwner = viewer.role === "owner";
  const staff = useQuery(api.users.list, isOwner ? {} : "skip");
  const clinics = useQuery(
    api.clinics.list,
    isOwner ? { includeInactive: true } : "skip",
  );

  const createUser = useMutation(api.users.create);
  const setRole = useMutation(api.users.setRole);
  const setActive = useMutation(api.users.setActive);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<Id<"users"> | null>(null);
  const { busy, run } = useBusyAction();

  if (!isOwner) {
    return (
      <div data-screen-state="ready" className="space-y-4">
        <SectionHeading title="Staff" />
        <Empty
          title="Staff and roles are owner-only"
          body="Roles decide who sees cost, margin and other clinics' data, so only an
                owner account can change them. Ask Dee or Gloria if yours is wrong."
        />
      </div>
    );
  }

  if (staff === undefined || clinics === undefined) {
    return (
      <div data-screen-state="loading">
        <Loading rows={5} label="Loading staff" />
      </div>
    );
  }

  const activeClinics = clinics.filter((clinic) => clinic.active);

  /* Clinics nobody can act for. A clinic with no active manager cannot approve
     a request; with no active assistant, nobody there can file one. Derived
     from the two live queries — never a stored flag. */
  const gaps = activeClinics
    .map((clinic) => {
      const people = staff.filter(
        (person) => person.active && person.clinicId === clinic._id,
      );
      return {
        clinic,
        manager: people.some((person) => person.role === "manager"),
        assistant: people.some((person) => person.role === "assistant"),
      };
    })
    .filter((row) => !row.manager || !row.assistant);

  const openDraft = (role: Role, clinicId: Id<"clinics"> | null) => {
    setEditing(null);
    setDraft({ name: "", email: "", role, clinicId });
  };

  /* Closing the draft and the editor happens inside the action, on the success
     path, so a refused write leaves the form open with what was typed in it. */
  const submitDraft = () => {
    if (!draft) return;
    void run(
      async () => {
        const result = await createUser({
          name: draft.name,
          email: draft.email,
          role: draft.role,
          clinicId: isClinicScoped(draft.role) ? draft.clinicId : null,
        });
        setDraft(null);
        return result;
      },
      {
        failed: "Could not add that person",
        done: (result) => ({
          happened: `${result.name} added`,
          next: `${roleLabel(result.role)}${
            result.clinicName ? ` at ${result.clinicName}` : " — all clinics"
          }.`,
        }),
      },
    );
  };

  const saveRole = (
    person: StaffRow,
    role: Role,
    clinicId: Id<"clinics"> | null,
  ) =>
    run(
      async () => {
        const result = await setRole({
          userId: person._id,
          role,
          clinicId: isClinicScoped(role) ? clinicId : null,
        });
        setEditing(null);
        return result;
      },
      {
        failed: "Could not change that role",
        done: (result) => ({
          happened: `${person.name} is now ${roleLabel(result.role).toLowerCase()}`,
          next: result.clinicName ? `At ${result.clinicName}.` : "All clinics.",
        }),
      },
    );

  const toggleActive = (person: StaffRow) =>
    run(
      async () => {
        await setActive({ userId: person._id, active: !person.active });
        setEditing(null);
      },
      {
        failed: "Could not change that account",
        done: person.active
          ? {
              happened: `${person.name} can no longer sign in`,
              next: "Their name stays on past requests, dispatches and the audit log.",
            }
          : `${person.name} is active again`,
      },
    );

  /* Grouped by clinic, cross-clinic people first. A flat list of eighteen names
     does not answer "who works at W68", which is the question this screen gets
     asked. */
  const groups: Array<{
    key: string;
    title: string;
    subtitle: string;
    rows: StaffRow[];
  }> = [];

  const crossClinic = staff.filter((person) => person.clinicId === null);
  if (crossClinic.length > 0) {
    groups.push({
      key: "all",
      title: "All clinics",
      subtitle: "Owners and the warehouse",
      rows: crossClinic,
    });
  }

  for (const clinic of [...clinics].sort((a, b) =>
    a.name.localeCompare(b.name),
  )) {
    const rows = staff.filter((person) => person.clinicId === clinic._id);
    if (rows.length === 0 && !clinic.active) continue;
    groups.push({
      key: clinic._id,
      title: clinic.name,
      subtitle: clinic.active
        ? clinic.code
        : `${clinic.code} · inactive clinic`,
      rows,
    });
  }

  const activeCount = staff.filter((person) => person.active).length;
  const unlinked = staff.filter(
    (person) => person.active && !person.linked,
  ).length;

  return (
    <div data-screen-state="ready" className="space-y-4 pb-8">
      <SectionHeading
        title="Staff"
        subtitle={`${activeCount} active across ${activeClinics.length} ${plural(
          activeClinics.length,
          "clinic",
        )}`}
        action={
          draft === null ? (
            <Button onClick={() => openDraft("assistant", null)}>
              Add someone
            </Button>
          ) : undefined
        }
      />

      {/* The reason this screen exists: a clinic nobody can act for. */}
      {gaps.length > 0 && draft === null ? (
        /* The last of the loud-banner pattern. It said the same thing with a
           2px amber border and an amber fill — the loudest object on a screen
           whose job is a list of people. Same words, same placement, same
           buttons; the amber is now a 4px left rule, which is how every other
           card in this app signals "look here". */
        <Card
          className={cn("block gap-0 space-y-3 border-l-4 p-4", TONE_EDGE.amber)}
        >
          <div>
            <p className="text-sm font-semibold">
              {gaps.length === 1
                ? "One clinic has nobody who can use it"
                : `${gaps.length} clinics have nobody who can use them`}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              A clinic with no manager cannot approve anything, and with no
              assistant nobody there can file a request.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {gaps.map(({ clinic, manager, assistant }) => (
              <div key={clinic._id} className="rounded-md bg-muted p-3">
                <div className="text-sm font-semibold">{clinic.name}</div>
                <div className="text-xs text-muted-foreground">
                  {!manager && !assistant
                    ? "No manager and no assistant"
                    : !manager
                      ? "No manager — requests cannot be approved"
                      : "No assistant — nobody can file a request"}
                </div>
                <div className="mt-2 flex gap-2">
                  {!manager ? (
                    <Button
                      variant="outline"
                      onClick={() => openDraft("manager", clinic._id)}
                    >
                      Add a manager
                    </Button>
                  ) : null}
                  {!assistant ? (
                    <Button
                      variant="outline"
                      onClick={() => openDraft("assistant", clinic._id)}
                    >
                      Add an assistant
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {draft !== null ? (
        <NewPersonForm
          draft={draft}
          clinics={activeClinics}
          busy={busy}
          onChange={setDraft}
          onSubmit={submitDraft}
          onCancel={() => setDraft(null)}
        />
      ) : null}

      {staff.length === 0 ? (
        <Empty
          title="Nobody on the staff list yet"
          body="Add an owner first — the account that adds everyone else."
        />
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.key}>
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold tracking-tight">
                  {group.title}
                </h2>
                <span className="text-xs text-muted-foreground">
                  {group.subtitle}
                </span>
              </div>

              {/* Cards on a phone; one bordered table per clinic on the
                  desktop, which is where "who works at W68, and what may they
                  see" is actually read. The rows opt into the table by losing
                  their own corners at `lg` — see `PersonCard`. */}
              <div className="space-y-2 lg:space-y-0 lg:overflow-hidden lg:rounded-xl lg:border lg:bg-card">
                {group.rows.length === 0 ? (
                  <Card className="block gap-0 p-4 text-sm text-muted-foreground lg:rounded-none lg:border-0">
                    Nobody here yet.
                  </Card>
                ) : (
                  group.rows
                    .slice()
                    .sort(
                      (a, b) =>
                        Number(b.active) - Number(a.active) ||
                        (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) ||
                        a.name.localeCompare(b.name),
                    )
                    .map((person) => (
                      <PersonCard
                        /* Keyed on the values the editor is seeded from, so a
                           change landing from the server — this list is
                           reactive — remounts the editor rather than leaving it
                           showing a role the person no longer has. */
                        key={`${person._id}:${person.role}:${person.clinicId ?? "none"}`}
                        person={person}
                        clinics={activeClinics}
                        open={editing === person._id}
                        busy={busy}
                        isSelf={person._id === viewer._id}
                        onToggle={() =>
                          setEditing(editing === person._id ? null : person._id)
                        }
                        onSaveRole={(role, clinicId) =>
                          saveRole(person, role, clinicId)
                        }
                        onToggleActive={() => toggleActive(person)}
                      />
                    ))
                )}
              </div>
            </section>
          ))}
        </div>
      )}

      {/*
        Said plainly, because the alternative is an owner adding a name and
        assuming that person can get in. A Convex session token from Clerk
        carries no email claim, so the staff row alone is not sign-in — see the
        note at the top of convex/users.ts.
      */}
      <p className="max-w-3xl text-xs leading-relaxed text-muted-foreground">
        Roles live in Nexora, not in Clerk — changing one here takes effect on
        that person's next request, with no deploy.
        {unlinked > 0
          ? ` ${unlinked} active ${unlinked === 1 ? "person has" : "people have"} no
             sign-in bound yet: run npm run clerk:users to create their Clerk
             account. It is safe to re-run.`
          : " Everyone active has a sign-in bound."}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

interface Draft {
  name: string;
  email: string;
  role: Role;
  clinicId: Id<"clinics"> | null;
}

function NewPersonForm({
  draft,
  clinics,
  busy,
  onChange,
  onSubmit,
  onCancel,
}: {
  draft: Draft;
  clinics: ClinicRow[];
  busy: boolean;
  onChange: (draft: Draft) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const clinicRequired = isClinicScoped(draft.role);
  const ready =
    draft.name.trim().length > 0 &&
    draft.email.trim().length > 0 &&
    (!clinicRequired || draft.clinicId !== null);

  return (
    /* Stock card, accent on the border — the same "this is the live thing on
       the screen" signal the new-clinic form uses, said with `--primary`
       rather than a 2px ring the registry has no equivalent of. */
    <Card className="block gap-0 space-y-3 border-primary p-4">
      <h2 className="font-semibold">Add someone</h2>

      <div className="grid gap-3 sm:grid-cols-2">
        {/* `aria-label` kept alongside the real `<label>` on both fields: the
            harness reads staff fields by `aria-label`, and the accessible name
            it expects must not change under it. */}
        <div className="grid gap-1.5">
          <Label htmlFor="staff-name">Name</Label>
          <Input
            id="staff-name"
            value={draft.name}
            onChange={(event) =>
              onChange({ ...draft, name: event.target.value })
            }
            placeholder="Priya Sandhu"
            aria-label="Staff name"
            autoComplete="off"
          />
        </div>

        <div className="grid gap-1.5">
          {/* `leading-snug` is the one class on a stock `<Label>` here: the
              registry sets `leading-none`, which is right for a one-word
              caption and puts two lines on top of each other when this one
              wraps at a narrow column. */}
          <Label htmlFor="staff-email" className="leading-snug">
            Work email — this is what they sign in with
          </Label>
          <Input
            id="staff-email"
            value={draft.email}
            onChange={(event) =>
              onChange({ ...draft, email: event.target.value })
            }
            placeholder="priya@clinic.example"
            aria-label="Staff email"
            inputMode="email"
            autoComplete="off"
          />
        </div>
      </div>

      <RolePicker
        role={draft.role}
        onChange={(role) =>
          onChange({
            ...draft,
            role,
            // An owner or warehouse admin is cross-clinic and the server refuses
            // one carrying a clinic, so drop it rather than send a doomed write.
            clinicId: isClinicScoped(role) ? draft.clinicId : null,
          })
        }
      />

      {clinicRequired ? (
        <ClinicPicker
          clinics={clinics}
          value={draft.clinicId}
          onChange={(clinicId) => onChange({ ...draft, clinicId })}
        />
      ) : null}

      {/* Stock sizes: this screen is the owner at a desk. */}
      <div className="flex gap-2 pt-1">
        <Button onClick={onSubmit} disabled={busy || !ready}>
          {busy ? "Adding…" : "Add to staff"}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

function RolePicker({
  role,
  onChange,
  label = "Role",
}: {
  role: Role;
  onChange: (role: Role) => void;
  label?: string;
}) {
  return (
    <div>
      <span className={FIELD_LABEL}>{label}</span>
      {/* Four registry buttons, selected/unselected as `default`/`outline`.
          `aria-pressed` stays — it is what says "one of these four is the
          current role" to a screen reader, and the kit's `Button` spreads it
          straight through to the element now. `justify-start` is the only
          class: the labels are words of very different lengths and centring
          them makes four buttons look like four different controls. */}
      <div className="mt-1 grid grid-cols-2 gap-2 lg:grid-cols-4">
        {ROLES.map((option) => (
          <Button
            key={option}
            variant={role === option ? "default" : "outline"}
            aria-pressed={role === option}
            onClick={() => onChange(option)}
            className="justify-start"
          >
            {ROLE_LABELS[option]}
          </Button>
        ))}
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        {ROLE_DESCRIPTIONS[role]}
      </p>
    </div>
  );
}

function ClinicPicker({
  clinics,
  value,
  onChange,
}: {
  clinics: ClinicRow[];
  value: Id<"clinics"> | null;
  onChange: (clinicId: Id<"clinics">) => void;
}) {
  if (clinics.length === 0) {
    return (
      <p className="rounded-md bg-amber-50 p-3 text-xs text-amber-900">
        There are no active clinics to assign anyone to. Add one on the Clinics
        screen first.
      </p>
    );
  }

  return (
    <div>
      <span className={FIELD_LABEL}>Clinic</span>
      {/* Same control as the role picker, same two variants, same
          `aria-pressed`. The pill shape is gone with it: a clinic is chosen
          from the same kind of row a role is, and two radii for one gesture
          made them look like two unrelated decisions. */}
      <div className="mt-1 flex flex-wrap gap-2">
        {clinics.map((clinic) => (
          <Button
            key={clinic._id}
            variant={value === clinic._id ? "default" : "outline"}
            aria-pressed={value === clinic._id}
            onClick={() => onChange(clinic._id)}
          >
            {clinic.name}
          </Button>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function PersonCard({
  person,
  clinics,
  open,
  busy,
  isSelf,
  onToggle,
  onSaveRole,
  onToggleActive,
}: {
  person: StaffRow;
  clinics: ClinicRow[];
  open: boolean;
  busy: boolean;
  isSelf: boolean;
  onToggle: () => void;
  onSaveRole: (role: Role, clinicId: Id<"clinics"> | null) => void;
  onToggleActive: () => void;
}) {
  const [role, setRole] = useState<Role>(
    isRole(person.role) ? person.role : "assistant",
  );
  const [clinicId, setClinicId] = useState<Id<"clinics"> | null>(
    person.clinicId,
  );

  const badge = ROLE_BADGE[person.role] ?? ROLE_BADGE.assistant;

  const changed =
    role !== person.role ||
    (isClinicScoped(role)
      ? clinicId !== person.clinicId
      : person.clinicId !== null);
  const complete = !isClinicScoped(role) || clinicId !== null;

  return (
    <div
      className={cn(
        "rounded-xl border lg:rounded-none lg:border-x-0 lg:border-t-0 lg:last:border-b-0",
        person.active ? "bg-card" : "bg-muted",
      )}
    >
      {/* A stacked card at 390px; one table row at `lg`.
       *
       * The three chips at the bottom of the card become three aligned columns
       * on the desktop via `lg:contents` on their wrapper — the wrapper stops
       * being a box and its children join the row's grid, so nineteen people
       * line up under role, clinic and sign-in without the phone layout being
       * rebuilt underneath a breakpoint.
       *
       * A raw <button> on purpose, and the reason is layout rather than
       * typography: the registry's button base is `inline-flex justify-center
       * whitespace-nowrap` with its own padding, and this element is a
       * six-column CSS grid spanning a card. Every one of those would have to
       * be undone for the columns to line up, which is fighting the primitive
       * rather than adopting it. It carries no icon, no disabled state and no
       * variant, so there is nothing left for `Button` to give it. The colours
       * are the theme's — `bg-muted/50` is the same hover the registry's own
       * table rows use. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 p-4 text-left text-sm transition-colors hover:bg-muted/50 active:bg-muted/50 lg:grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)_8.5rem_minmax(0,8rem)_9rem_1rem] lg:items-center lg:gap-x-4 lg:px-4 lg:py-2.5"
      >
        <div className="min-w-0 flex-1 lg:contents">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={cn(
                "truncate font-medium",
                person.active ? "" : "text-muted-foreground line-through",
              )}
            >
              {person.name}
            </span>
            {isSelf ? (
              <span className="shrink-0 text-xs font-semibold text-muted-foreground">
                you
              </span>
            ) : null}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {person.email}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 lg:contents">
            {/* Stock badge, stock variants — see `ROLE_BADGE`. A deactivated
                person's role reads muted whatever the role was, because the
                fact worth carrying at that point is that they are gone. */}
            <Badge
              variant={person.active ? badge.variant : "secondary"}
              className={person.active ? badge.className : "text-muted-foreground"}
            >
              {roleLabel(person.role)}
            </Badge>
            {person.clinicName ? (
              <span className="truncate text-xs text-muted-foreground">
                {person.clinicName}
              </span>
            ) : (
              /* An empty cell, desktop only: without it the sign-in column
                 slides left for the cross-clinic people and the table stops
                 being a table. */
              <span aria-hidden className="hidden lg:block" />
            )}
            {!person.active ? (
              <Badge variant="secondary" className="text-muted-foreground">
                deactivated
              </Badge>
            ) : person.linked ? (
              <span className="text-xs text-muted-foreground">
                sign-in linked
              </span>
            ) : (
              <span className="text-xs text-amber-700">
                sign-in not linked yet
              </span>
            )}
          </div>
        </div>
        <span aria-hidden className="shrink-0 text-muted-foreground">
          {open ? "▲" : "▼"}
        </span>
      </button>

      {open ? (
        <div className="space-y-3 border-t p-4 lg:bg-muted/50">
          {person.active ? (
            <>
              <RolePicker role={role} onChange={setRole} label="Change role" />
              {isClinicScoped(role) ? (
                <ClinicPicker
                  clinics={clinics}
                  value={clinicId}
                  onChange={setClinicId}
                />
              ) : null}
              {/* Each button in its own row: the panel stacks on `space-y-3`,
                  and a stock button is `inline-flex`, so two bare ones would
                  share a line. Stock sizes throughout — this is a desk. */}
              <div>
                <Button
                  disabled={busy || !changed || !complete}
                  onClick={() => onSaveRole(role, clinicId)}
                >
                  {changed ? "Save role" : "No change"}
                </Button>
              </div>
            </>
          ) : null}

          <div>
            <Button
              variant={person.active ? "destructive" : "outline"}
              disabled={busy}
              onClick={onToggleActive}
            >
              {person.active ? "Deactivate" : "Reactivate"}
            </Button>
          </div>

          <p className="max-w-2xl text-xs text-muted-foreground">
            {person.active
              ? "Deactivating revokes sign-in immediately. Their name stays on past requests, dispatches and the audit log — nothing is deleted."
              : "Reactivating restores sign-in with the same role and clinic."}
          </p>
        </div>
      ) : null}
    </div>
  );
}
